import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ContextButtonConnected } from "@/components/molecules/ContextButtonConnected";
import { PagePickerDialog } from "@/components/molecules/PagePickerDialog";
import { ContextPanelContent } from "@/components/organisms/ContextPanelContent";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useDomains } from "@/hooks/useDomains";
import { useOffers } from "@/hooks/useOffers";
import { usePersonas } from "@/hooks/usePersonas";
import { usePolicies } from "@/hooks/usePolicies";
import { useUI } from "@/hooks/useUI";
import { useUser } from "@/hooks/useUser";
import {
  hasActivePendingOffers,
  hasActivePendingPersonas,
} from "@/lib/generationState";
import {
  OfferGenerationService,
} from "@/lib/offerGenerationService";
import { PersonaGenerationService } from "@/lib/personaGenerationService";
import type { Page } from "@/schemas";
import { Policy } from "@/schemas/firestore/policy.schema";
import { useStore } from "@/store";

export interface ContextSidebarProps {
  /**
   * Additional CSS classes for the trigger button
   */
  className?: string;
  /**
   * External control over sidebar open state
   */
  isOpen?: boolean;
  /**
   * Callback when sidebar open state changes
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Full Context Sidebar organism that combines:
 * - ContextButtonConnected molecule as trigger
 * - Complete sidebar logic with auto-expand
 * - All business operations and state management
 *
 * Use only once per page to avoid duplicate dialogs.
 */
export function ContextSidebar({
  className,
  isOpen,
  onOpenChange,
}: ContextSidebarProps) {
  // State management - use external state if provided, otherwise internal state
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(false);
  const contextSidebarOpen =
    isOpen !== undefined ? isOpen : internalSidebarOpen;
  const setContextSidebarOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setInternalSidebarOpen(open);
      }
    },
    [onOpenChange],
  );
  const [showPersonaImportForm, setShowPersonaImportForm] = useState(false);
  const [showOfferImportForm, setShowOfferImportForm] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  // Personas and offers are AI-generated only (backend-write-only collections),
  // so policies are the only thing that can be edited in place.
  const [editingItem, setEditingItem] = useState<{
    id: string;
    type: "policy";
  } | null>(null);

  // Track if this is the initial mount to avoid resetting session state on re-renders
  const isInitialMount = useRef(true);
  // Track previous domain ID to detect domain changes
  const prevDomainId = useRef<string | null>(null);

  const {
    shouldShowContextSidebar,
    dismissContextSidebarForDomain,
    resetSessionState,
  } = useUI();
  const { currentUser } = useUser();
  const orgId = useStore((state) => state.orgId);

  const { currentDomain } = useDomains();
  const pages = useStore((state) => state.pages);

  const pickablePages = useMemo<Page[]>(
    () =>
      currentDomain?.id
        ? pages.filter((p) => p.domainId === currentDomain.id && !!p.url)
        : [],
    [pages, currentDomain?.id],
  );

  type PendingContextAction =
    | { type: "generatePersonas" }
    | { type: "importPersona"; description: string }
    | { type: "generateOffer" }
    | { type: "importOffer"; description: string };

  const [pendingAction, setPendingAction] =
    useState<PendingContextAction | null>(null);
  const [isExecutingAction, setIsExecutingAction] = useState(false);

  const {
    personas,
    isLoading: isLoadingPersonas,
    subscribeToPersonas,
    setPersonaEnabled,
    clearPersonas,
  } = usePersonas(currentDomain?.id);

  const {
    offers,
    isLoading: isLoadingOffers,
    subscribeToOffers,
    setOfferEnabled,
    clearOffers,
  } = useOffers(currentDomain?.id);

  const {
    policies,
    isLoading: isLoadingPolicies,
    createPolicy,
    editPolicy,
    removePolicy,
    subscribe: subscribeToPolicies,
    clearPolicies,
  } = usePolicies(orgId ?? undefined, currentDomain?.id);

  // Computed generation states based on pending documents
  // Add defensive checks to prevent state loss during navigation
  const isGeneratingPersonas =
    personas && personas.length >= 0
      ? hasActivePendingPersonas(personas)
      : false;
  const isGeneratingOffers =
    offers && offers.length >= 0 ? hasActivePendingOffers(offers) : false;

  // Subscribe to personas when domain changes
  useEffect(() => {
    if (orgId && currentDomain?.id) {
      const unsubscribe = subscribeToPersonas(orgId, currentDomain.id);
      return unsubscribe;
    } else {
      clearPersonas();
    }
  }, [orgId, currentDomain?.id, subscribeToPersonas, clearPersonas]);

  // Subscribe to offers when domain changes
  useEffect(() => {
    if (orgId && currentDomain?.id) {
      const unsubscribe = subscribeToOffers(orgId, currentDomain.id);
      return unsubscribe;
    } else {
      clearOffers();
    }
  }, [orgId, currentDomain?.id, subscribeToOffers, clearOffers]);

  // Subscribe to policies when domain changes
  useEffect(() => {
    if (orgId && currentDomain?.id) {
      const unsubscribe = subscribeToPolicies();
      return unsubscribe;
    } else {
      clearPolicies();
    }
  }, [orgId, currentDomain?.id, subscribeToPolicies, clearPolicies]);

  // Note: Global subscription cleanup (unsubscribePersonas/Offers/Policies) is
  // intentionally NOT done here. useStoreInitializer manages subscriptions
  // centrally, and tearing them all down on ContextSidebar unmount causes
  // data loss when navigating to the setup wizard (personas step breaks).
  // Per-domain cleanups above handle individual subscription lifecycle.

  // Auto-expand sidebar for fresh workspace (handles both page load and domain changes)
  useEffect(() => {
    const currentDomainId = currentDomain?.id;

    // Only reset session state on the very first mount (genuine page load)
    // This prevents the sidebar from reopening when the component re-mounts
    if (isInitialMount.current) {
      resetSessionState();
      isInitialMount.current = false;
    }

    // Don't proceed if we don't have a domain yet
    if (!currentDomainId) {
      return;
    }

    // Don't check if data is still loading
    if (isLoadingPersonas || isLoadingOffers || isLoadingPolicies) {
      return;
    }

    // Don't auto-expand if sidebar is already open
    if (contextSidebarOpen) {
      return;
    }

    // Update previous domain ID for next comparison
    prevDomainId.current = currentDomainId;

    // Check if sidebar should auto-expand with delay to ensure all data is loaded
    const timer = setTimeout(() => {
      // Double-check conditions before opening
      if (!contextSidebarOpen && shouldShowContextSidebar(currentDomainId)) {
        setContextSidebarOpen(true);
      }
    }, 1000); // 1 second delay to ensure data is loaded

    return () => clearTimeout(timer);
  }, [
    currentDomain?.id, // Add domain ID to dependencies to trigger on domain changes
    isLoadingPersonas,
    isLoadingOffers,
    isLoadingPolicies,
    contextSidebarOpen, // Add this to prevent opening when already open
    personas.length, // Direct dependencies instead of function dependencies
    offers.length,
    policies.length,
    resetSessionState,
    shouldShowContextSidebar,
    setContextSidebarOpen,
  ]);

  // Derive selected personas, offers, and policies from current domain
  const selectedPersonas = personas
    .filter((p) => p.is_enabled)
    .map((p) => p.id);

  const selectedOffers = offers.filter((o) => o.is_enabled).map((o) => o.id);

  const selectedPolicies = policies
    .filter((p) => p.is_enabled)
    .map((p) => p.id);

  // Handle importing new persona (opens import form)
  const handleImportPersona = () => {
    setShowPersonaImportForm(true);
    setEditingItem(null);
  };

  // Handle import persona submission — defer backend call to picker confirm
  const handleImportPersonaSubmit = async (description: string) => {
    setShowPersonaImportForm(false);
    setPendingAction({ type: "importPersona", description });
  };

  // Handle generating personas — defer to picker confirm
  const handleGeneratePersonas = async () => {
    if (isGeneratingPersonas) return;
    setPendingAction({ type: "generatePersonas" });
  };

  // Handle toggling persona selection
  const handleTogglePersona = async (id: string) => {
    if (!currentDomain?.id) return;

    const persona = personas.find((p) => p.id === id);
    if (persona) {
      try {
        await setPersonaEnabled(currentDomain.id, id, !persona.is_enabled);
      } catch (error) {
        console.error("Error toggling persona:", error);
      }
    }
  };

  // Handle importing new offer (opens import form)
  const handleImportOffer = () => {
    setShowOfferImportForm(true);
    setEditingItem(null);
  };

  // Handle import offer submission — defer backend call to picker confirm
  const handleImportOfferSubmit = async (description: string) => {
    setShowOfferImportForm(false);
    setPendingAction({ type: "importOffer", description });
  };

  // Handle generating offer — defer to picker confirm
  const handleGenerateOffer = async () => {
    if (isGeneratingOffers) return;
    setPendingAction({ type: "generateOffer" });
  };

  const executeWithPage = async (page: Page) => {
    if (
      !pendingAction ||
      !currentUser?.uid ||
      !currentDomain?.id ||
      !page.url
    ) {
      setPendingAction(null);
      return;
    }

    const action = pendingAction;
    setIsExecutingAction(true);
    try {
      switch (action.type) {
        case "generatePersonas": {
          await PersonaGenerationService.generateDualPersonas({
            page_url: page.url,
            domain_id: currentDomain.id,
            page_id: page.id,
          });
          break;
        }
        case "importPersona": {
          await PersonaGenerationService.generateCustomPersona({
            page_url: page.url,
            domain_id: currentDomain.id,
            page_id: page.id,
            persona_description: action.description,
          });
          break;
        }
        case "generateOffer": {
          await OfferGenerationService.generateOffer({
            page_url: page.url,
            domain_id: currentDomain.id,
            page_id: page.id,
          });
          break;
        }
        case "importOffer": {
          await OfferGenerationService.generateCustomOffer({
            page_url: page.url,
            domain_id: currentDomain.id,
            page_id: page.id,
            offer_description: action.description,
          });
          break;
        }
      }
    } catch (error) {
      console.error("Context action failed:", error);
    } finally {
      setIsExecutingAction(false);
      setPendingAction(null);
    }
  };

  const pickerCopy = (() => {
    switch (pendingAction?.type) {
      case "generatePersonas":
        return {
          title: "Generate Personas",
          description:
            "Pick the page these personas should be generated for.",
          confirmLabel: "Generate",
        };
      case "importPersona":
        return {
          title: "Import Persona",
          description:
            "Pick the page this imported persona should be attached to.",
          confirmLabel: "Import",
        };
      case "generateOffer":
        return {
          title: "Generate Offer",
          description: "Pick the page this offer should be generated for.",
          confirmLabel: "Generate",
        };
      case "importOffer":
        return {
          title: "Import Offer",
          description:
            "Pick the page this imported offer should be attached to.",
          confirmLabel: "Import",
        };
      default:
        return { title: "", description: "", confirmLabel: "" };
    }
  })();

  // Handle toggling offer selection
  const handleToggleOffer = async (id: string) => {
    if (!currentDomain?.id) return;

    const offer = offers.find((o) => o.id === id);
    if (offer) {
      try {
        await setOfferEnabled(currentDomain.id, id, !offer.is_enabled);
      } catch (error) {
        console.error("Error toggling offer:", error);
      }
    }
  };

  // Handle adding new policy
  const handleAddPolicy = () => {
    setShowPolicyForm(true);
    setEditingItem(null);
  };

  // Handle editing policy
  const handleEditPolicy = (id: string) => {
    const policy = policies.find((p: Policy) => p.id === id);
    if (policy) {
      setShowPolicyForm(true);
      setEditingItem({ id, type: "policy" });
    }
  };

  // Handle toggling policy selection
  const handleTogglePolicy = async (id: string) => {
    if (!currentUser?.uid || !currentDomain?.id) return;

    const policy = policies.find((p) => p.id === id);
    if (policy) {
      try {
        await editPolicy(id, {
          is_enabled: !policy.is_enabled,
        });
      } catch (error) {
        console.error("Error toggling policy:", error);
      }
    }
  };

  // Handle deleting a policy
  const handleDeletePolicy = async (id: string) => {
    try {
      await removePolicy(id);
    } catch (error) {
      console.error("Error deleting policy:", error);
    }
  };

  // Handle save policy
  const handleSavePolicy = async (
    policyData: Omit<
      Policy,
      "id" | "created_at" | "updated_at" | "created_by" | "updated_by" | "domain_id"
    >,
  ) => {
    try {
      if (editingItem) {
        // Update existing policy
        await editPolicy(editingItem.id, policyData);
      } else {
        // Add new policy
        await createPolicy(policyData);
      }
      setShowPolicyForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error("Error saving policy:", error);
    }
  };

  // Handle back to main context view
  const handleBackToContext = () => {
    setShowPersonaImportForm(false);
    setShowOfferImportForm(false);
    setShowPolicyForm(false);
    setEditingItem(null);
  };

  // Handle Apply button - close sidebar and dismiss for current domain
  const handleApplyContext = () => {
    setContextSidebarOpen(false);
    if (currentDomain?.id) {
      dismissContextSidebarForDomain(currentDomain.id);
    }
  };

  // Always render the button - external state control just overrides the internal state
  const shouldRenderButton = true;

  return (
    <>
      {shouldRenderButton && (
        <ContextButtonConnected
          onClick={() => setContextSidebarOpen(!contextSidebarOpen)}
          className={className}
        />
      )}

      {/* Context Sidebar */}
      <Sheet
        open={contextSidebarOpen}
        onOpenChange={(open) => {
          setContextSidebarOpen(open);
          // If closing the sidebar, dismiss it for this domain
          if (!open && currentDomain?.id) {
            dismissContextSidebarForDomain(currentDomain.id);
          }
        }}
      >
        <SheetContent className="flex flex-col p-0 w-full sm:w-[400px] sm:max-w-[540px] h-[100dvh] max-h-[100dvh]">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <ContextPanelContent
              showPersonaImportForm={showPersonaImportForm}
              showOfferImportForm={showOfferImportForm}
              showPolicyForm={showPolicyForm}
              editingItem={editingItem}
              personas={personas}
              offers={offers}
              policies={policies}
              selectedPersonas={selectedPersonas}
              selectedOffers={selectedOffers}
              selectedPolicies={selectedPolicies}
              isLoadingPersonas={isLoadingPersonas}
              isGeneratingPersonas={isGeneratingPersonas}
              isLoadingOffers={isLoadingOffers}
              isGeneratingOffers={isGeneratingOffers}
              isLoadingPolicies={isLoadingPolicies}
              isImportingPersona={false}
              isImportingOffer={false}
              onSavePolicy={handleSavePolicy}
              onBackToContext={handleBackToContext}
              onImportPersona={handleImportPersona}
              onImportPersonaSubmit={handleImportPersonaSubmit}
              onImportOffer={handleImportOffer}
              onImportOfferSubmit={handleImportOfferSubmit}
              onAddPolicy={handleAddPolicy}
              onEditPolicy={handleEditPolicy}
              onDeletePolicy={handleDeletePolicy}
              onTogglePersona={handleTogglePersona}
              onToggleOffer={handleToggleOffer}
              onTogglePolicy={handleTogglePolicy}
              onGeneratePersonas={handleGeneratePersonas}
              onGenerateOffer={handleGenerateOffer}
            />
          </div>

          {/* Fixed Footer with Apply Button */}
          <div className="flex-shrink-0 p-4 sm:p-6 border-t border-border bg-background">
            <Button onClick={handleApplyContext} className="w-full">
              Apply
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <PagePickerDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        pages={pickablePages}
        onConfirm={executeWithPage}
        isConfirming={isExecutingAction}
        title={pickerCopy.title}
        description={pickerCopy.description}
        confirmLabel={pickerCopy.confirmLabel}
      />
    </>
  );
}
